import { useEffect, useState } from "react"
import { Loader2 } from "lucide-react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { zodResolver } from "@hookform/resolvers/zod"

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@f2e/ui/components/base"

import { Workspace, AgentType, Agent } from "@/types"
import { useToast } from "@/hooks"
import {
  useCreateAgentApi,
  makeCreateAgentRequest,
  agentResponseToAgent,
} from "@/hooks/api"
import { getUserFacingApiErrorMessage } from "@/lib/apiErrors"

import { BasicFormSchema, BasicFormType } from "./type"
import FormFactory from "./FormFactory"
import type { KnowledgeBaseFolder } from "@/hooks/api"

type DialogInfo = {
  title: string
  description: string
  isEditMode: boolean
  confirmButtonText: string
}

interface CreateDialogProps {
  agentType: AgentType
  workspace: Workspace
  knowledgeBaseMap: Map<string, KnowledgeBaseFolder>
  open?: boolean
  onOpenChange?: (open: boolean) => void
  onConfirm?: (agent: Agent) => void
}

const CreateDialog = ({
  agentType,
  workspace,
  knowledgeBaseMap,
  open = false,
  onOpenChange,
  onConfirm,
}: CreateDialogProps) => {
  const { t } = useTranslation()
  const { alertToast } = useToast()
  const [isProcessing, setIsProcessing] = useState(false)

  const form = useForm<BasicFormType>({
    resolver: zodResolver(BasicFormSchema),
    defaultValues: {
      name: "",
      description: "",
      systemPrompt: "",
      attachments: [],
      unifiedTools: [],
      knowledgeNodeIds: [],
      suggestedQuestions: [],
      aiTakeoverGreeting: "",
    },
  })

  const [dialogInfo, setDialogInfo] = useState<DialogInfo>()
  const [show, setShow] = useState(open || false)

  // APIs
  const {
    error: createBasicAgentError,
    data: createBasicAgentRespData,
    fetchFn: createBasicAgent,
  } = useCreateAgentApi()

  useEffect(() => {
    setShow(open || false)
    form.reset()

    const dialogInfo: DialogInfo = {
      title: t("workspace.agent.dialog.new.title"),
      description: t("workspace.agent.dialog.new.description"),
      isEditMode: false,
      confirmButtonText: t("workspace.create"),
    }
    setDialogInfo(dialogInfo)
  }, [open, form, setDialogInfo, t])

  // handle creating basic agent response
  useEffect(() => {
    if (!createBasicAgentError) return
    console.error("Failed to create an agent", createBasicAgentError)

    alertToast({
      title: t("workspace.agent.message.createFailed.title"),
      description: getUserFacingApiErrorMessage(
        createBasicAgentError,
        t,
        createBasicAgentRespData?.message ||
          t("workspace.agent.message.createFailed.title")
      ),
    })
  }, [createBasicAgentError, createBasicAgentRespData, alertToast, t])

  const handleConfirmClick = (
    _agentType: AgentType,
    formData: BasicFormType
  ) => {
    setIsProcessing(true)
    handleConfirmBasic(formData)
  }

  const handleConfirmBasic = (formData: BasicFormType) => {
    const req = makeCreateAgentRequest(
      formData.name,
      formData.description,
      workspace.id,
      formData.systemPrompt,
      formData.unifiedTools,
      formData.attachments,
      formData.llmModel,
      formData.llmModelSettings,
      formData.suggestedQuestions,
      formData.humanHandoff,
      formData.outputPresentationPolicy,
      [],
      formData.responseMode,
      formData.aiTakeoverGreeting
    )
    createBasicAgent({
      jsonBody: req,
      callbackFn: (resp) => {
        if (!resp || !resp.data) return

        setIsProcessing(false)
        onConfirm?.(agentResponseToAgent(resp.data))
        handleOpenChange(false)
      },
    })
  }

  const handleOpenChange = (open: boolean) => {
    onOpenChange?.(open)
    if (!open) {
      setShow(false)
      form.reset()
    }
  }

  if (!knowledgeBaseMap || !dialogInfo) return null

  return (
    <Dialog open={show} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-[550px] overflow-y-auto overflow-visible">
        <DialogHeader>
          <DialogTitle>{dialogInfo.title}</DialogTitle>
          <DialogDescription>{dialogInfo.description}</DialogDescription>
        </DialogHeader>
        <FormFactory
          agentType={agentType}
          workspace={workspace}
          knowledgeBaseMap={knowledgeBaseMap}
          agents={[]}
          onConfirm={handleConfirmClick}
        >
          <DialogFooter className="mt-4">
            {form.formState.disabled ? (
              <Button
                variant="outline"
                type="button"
                onClick={() => handleOpenChange(false)}
              >
                Close
              </Button>
            ) : (
              <Button type="submit" disabled={isProcessing}>
                {isProcessing ? (
                  <>
                    <Loader2 className="size-4 mr-2 animate-spin" />
                    {"Processing..."}
                  </>
                ) : (
                  dialogInfo.confirmButtonText
                )}
              </Button>
            )}
          </DialogFooter>
        </FormFactory>
      </DialogContent>
    </Dialog>
  )
}

export { CreateDialog }
export default CreateDialog
